import { useEffect, useState } from 'react';
import { Button, Stack, TextInput } from '@mantine/core';
import type {
  CounterpartyCreateInput,
  CounterpartyDto,
  CounterpartyPatchInput,
  CounterpartyPartyType,
} from '@expeditor/shared';
import { counterpartiesApi } from '../../shared/api/reference-books';
import { ReferenceBookTable } from './ReferenceBookTable';
import {
  applyCompanySuggestion,
  PartyRequisitesFields,
  partyTypeLabel,
} from '../../shared/ui/PartyRequisitesFields';
import { CompanyLookup } from '../../shared/ui/CompanyLookup';
import {
  AddressBreakdownFields,
  useAddressBreakdown,
} from '../../shared/ui/AddressBreakdownFields';

export function CounterpartiesPage() {
  return (
    <ReferenceBookTable<CounterpartyDto, CounterpartyCreateInput, CounterpartyPatchInput>
      title="Мои контрагенты"
      api={counterpartiesApi}
      columns={[
        { header: 'Название', render: (i) => i.label },
        { header: 'Полное имя', render: (i) => i.name },
        { header: 'Тип', render: (i) => partyTypeLabel(i.partyType) },
        { header: 'ИНН', render: (i) => i.inn ?? '—' },
        { header: 'Страна', render: (i) => i.country ?? '—' },
        { header: 'Адрес', render: (i) => i.address ?? '—' },
      ]}
      renderForm={CounterpartyForm}
    />
  );
}

function CounterpartyForm({
  initial,
  onSubmit,
  submitting,
}: {
  initial: CounterpartyDto | null;
  onSubmit: (dto: CounterpartyCreateInput | CounterpartyPatchInput) => Promise<void>;
  submitting: boolean;
}) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [partyType, setPartyType] = useState<string>(initial?.partyType ?? '');
  const [inn, setInn] = useState(initial?.inn ?? '');
  const [kpp, setKpp] = useState(initial?.kpp ?? '');
  const [ogrn, setOgrn] = useState(initial?.ogrn ?? '');
  const [country, setCountry] = useState(initial?.country ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [contactName, setContactName] = useState(initial?.contactName ?? '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone ?? '');
  // Разбивка адреса (для ЭТрН) + телефон для ЭПД — одна на текст адреса.
  const { parts, setParts, save: saveParts } = useAddressBreakdown(initial?.address);

  useEffect(() => {
    setLabel(initial?.label ?? '');
    setName(initial?.name ?? '');
    setPartyType(initial?.partyType ?? '');
    setInn(initial?.inn ?? '');
    setKpp(initial?.kpp ?? '');
    setOgrn(initial?.ogrn ?? '');
    setCountry(initial?.country ?? '');
    setAddress(initial?.address ?? '');
    setContactName(initial?.contactName ?? '');
    setContactPhone(initial?.contactPhone ?? '');
  }, [initial?.id]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // Разбивка живёт отдельно (одна на текст адреса) — сохраняем рядом с записью.
    void saveParts(address).catch(() => undefined);
    void onSubmit({
      label,
      name,
      partyType: (partyType || undefined) as CounterpartyPartyType | undefined,
      inn: inn || undefined,
      kpp: kpp || undefined,
      ogrn: ogrn || undefined,
      country: country || undefined,
      address: address || undefined,
      contactName: contactName || undefined,
      contactPhone: contactPhone || undefined,
    });
  };

  return (
    <form onSubmit={submit}>
      <Stack>
        <TextInput
          label="Краткое название"
          required
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
        />
        <CompanyLookup
          onSelect={(s) => {
            applyCompanySuggestion(s, {
              setName,
              setCountry,
              setAddress,
              setType: setPartyType,
              setInn,
              setKpp,
              setOgrn,
            });
            if (!label) setLabel(s.shortName || s.fullName);
          }}
        />
        <TextInput
          label="Полное наименование / ФИО"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <PartyRequisitesFields
          type={partyType}
          setType={setPartyType}
          inn={inn}
          setInn={setInn}
          kpp={kpp}
          setKpp={setKpp}
          ogrn={ogrn}
          setOgrn={setOgrn}
        />
        <TextInput
          label="Страна"
          placeholder="Россия"
          value={country}
          onChange={(e) => setCountry(e.currentTarget.value)}
        />
        <TextInput
          label="Адрес"
          value={address}
          onChange={(e) => setAddress(e.currentTarget.value)}
        />
        <AddressBreakdownFields value={parts} onChange={setParts} />
        <TextInput
          label="Контактное лицо"
          value={contactName}
          onChange={(e) => setContactName(e.currentTarget.value)}
        />
        <TextInput
          label="Телефон"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.currentTarget.value)}
        />
        <Button type="submit" loading={submitting}>
          Сохранить
        </Button>
      </Stack>
    </form>
  );
}
